/**
 * Antila33_3.ts — anchor points for the Antila 33.3 sailboat preset.
 *
 * Body-frame convention used here:
 *   +X = forward (bow)
 *   +Y = up
 *   +Z = starboard (right)
 *   Y = 0 → designed waterline.
 *
 * Source dimensions (manufacturer data):
 *   - hull length 9.60 m, beam 3.24 m, draft 0.45 m (centerboard up),
 *   - max cabin height 1.95 m, mast 14.45 m above WL.
 *
 * Anchor positions are PLACEHOLDERS — they're geometrically plausible for a
 * 9.6 m centerboard cruiser, but should be re-derived from named Empties in
 * `antila_33_3.glb` (Cleat.Bow, Cleat.Stern.*, Skipper.Head, …) once those
 * are authored.
 */

import type { BoatAnchorPoints, Vec3 } from "./BoatAnchorPoints";

export function antila33_3Anchors(): BoatAnchorPoints {
  // ---- Mass / buoyancy reference ------------------------------------
  const centerOfMass: Vec3 = [-0.6, -0.2, 0];
  const centerOfBuoyancy: Vec3 = [0, -0.5, 0];

  // ---- Cleats -------------------------------------------------------
  // Bow / stern at ~±48% LOA from CG; midship near max-beam station.
  const cleats = [
    { name: "bow", position: [4.6, 0.95, 0] as Vec3 },
    { name: "stern_port", position: [-4.5, 0.85, -1.55] as Vec3 },
    { name: "stern_starboard", position: [-4.5, 0.85, 1.55] as Vec3 },
    { name: "midship_port", position: [0.6, 0.95, -1.62] as Vec3 },
    { name: "midship_starboard", position: [0.6, 0.95, 1.62] as Vec3 },
    { name: "forepeak", position: [3.7, 1.05, 0] as Vec3 },
  ];

  // ---- Fender hangers ----------------------------------------------
  const fenderXs = [3.4, 1.2, -1.2, -3.4];
  const fenderHangers = fenderXs.flatMap((xFwd) => [
    { side: "port" as const, position: [xFwd, 0.95, -1.7] as Vec3 },
    { side: "starboard" as const, position: [xFwd, 0.95, 1.7] as Vec3 },
  ]);

  // ---- Steering / propulsion ---------------------------------------
  const rudderPivot: Vec3 = [-4.55, -0.3, 0];
  const tillerHandle: Vec3 = [-3.0, 0.4, 0];
  const centerboardPivot: Vec3 = [0.25, -0.1, 0];
  const engineMount: Vec3 = [-4.7, -0.2, 0];
  const propellerPos: Vec3 = [-5.05, -0.55, 0];

  // ---- Rig ---------------------------------------------------------
  const mastBase: Vec3 = [0.95, 0.75, 0];
  const mastTop: Vec3 = [0.95, 14.45, 0];
  const boomAttach: Vec3 = [0.95, 1.95, 0];

  // ---- Buoyancy samples (8 pts) ------------------------------------
  const buoyancySamples: Vec3[] = [
    [4.4, -0.65, 0],
    [-4.4, -0.65, 0],
    [2.3, -0.65, -1.0],
    [2.3, -0.65, 1.0],
    [-2.3, -0.65, -1.0],
    [-2.3, -0.65, 1.0],
    [0, -0.3, -1.55],
    [0, -0.3, 1.55],
  ];

  // ---- Wind application --------------------------------------------
  // CE of cabin + mast/rig roughly 2.4 m above WL, slightly forward of CG.
  const windApplicationLateral: Vec3 = [0.3, 2.4, 0];
  const windApplicationFrontal: Vec3 = [0.6, 1.6, 0];

  // ---- Camera ------------------------------------------------------
  // Fallback cockpit eye if the glb has no `Skipper.Head` Empty:
  // helmsman standing at the wheel, ~1.55 m above WL.
  const cameraCockpit: Vec3 = [-3.6, 1.55, 0];
  const cameraChaseOffset: Vec3 = [-9.0, 3.5, 0];

  return {
    presetId: "antila_33_3",
    centerOfMass,
    centerOfBuoyancy,
    cleats,
    fenderHangers,
    rudderPivot,
    tillerHandle,
    centerboardPivot,
    engineMount,
    propellerPos,
    mastBase,
    mastTop,
    boomAttach,
    buoyancySamples,
    windApplicationLateral,
    windApplicationFrontal,
    cameraCockpit,
    cameraChaseOffset,
  };
}

/** Lookup table for `BoatSpec.anchorPreset`. */
import { antila30_1Anchors } from "./Antila30_1";

export const ANCHOR_PRESETS: Record<string, () => BoatAnchorPoints> = {
  antila_33_3: antila33_3Anchors,
  antila_30_1: antila30_1Anchors,
};
